// SectionTitle — THE section-title format (Alex, 2026-07-20: "I would like to
// have all these four, five, or six sections have the title in the same
// format"). Before this, four sections (Product/Onshape/Configuration/
// Components) already agreed on one look — small, bold, uppercase, letter-
// spaced — but it was copy-pasted inline in three different files, and two
// more sections (Where used / Used in products) used a DIFFERENT look
// (body-size, not uppercase, a decorative icon), and two more (the Onshape
// right panel's item identity, and its BOM/Prepare block) had no title at
// all. This is the one place the format lives now; every section title in
// the app renders through it.
//
//   children — the title text
//   count    — optional number, rendered muted after the title (e.g. "(12)")
//   right    — optional node right-aligned in the same row (a button, a hint)
//   icon     — optional node rendered BEFORE the title (the item's picture).
//              Outside the collapse control on purpose — see the note below.
//
// COLLAPSE (UI_VISION §4: "Titled, and the title is the collapse control").
// Both of these are OPTIONAL and absent by default, so every existing call
// site renders byte-identically:
//   onToggle — makes the title a button with the shared Chevron. Omit = plain title.
//   collapsed — current state, only meaningful with onToggle.
//   na / naReason — the section can never apply here (UI_VISION §4's "not
//     applicable", as opposed to "not yet linked"). Renders the title muted
//     with a `#NA` marker and NO toggle. `#NA` is deliberate reuse of the
//     three-state empty-cell vocabulary (#NA / — / warning) one layer up,
//     which is exactly how §4 frames it. Alex is colour-blind, so the state
//     is carried by the WORD `#NA` and by the missing chevron — never by the
//     muting alone.
//   locked — always-open sections (Alex, 2026-08-20: Product's own identity
//     row "must be open" but should still read IN LINE with every collapsible
//     title). Shows the same Chevron, fixed open, but as a plain span rather
//     than a button — no cursor pointer, no aria-expanded, nothing implying a
//     click that would do nothing.
//   collapsedLabel — optional word shown ONLY while collapsed (e.g. "Link" /
//     "Sync"), so the closed state names the action a click will take instead
//     of offering a bare chevron (COS-1: "closed with something like a button
//     w/ 'Link' or 'Sync' to open"). Generic on purpose — the caller decides
//     the word; this primitive only decides where it goes. Absent by default,
//     so every existing collapsible title is unchanged.
//   problem — THE problem-title style (Alex, 2026-10-11: "Can the 'Not Sync /
//     TBD / 12 Not Sync' be red & Bold! need to create another type of style
//     for card title issues"). A section whose content is something WRONG that
//     waits on someone says so in its title: ⚠ before the title, title and
//     count in the warning colour, the count bold. The caller passes it only
//     while something is actually open, so a clean section reads as a normal
//     title. Colour-blind safe (Alex is): the ⚠ glyph and the bold count carry
//     it; the red only reinforces. Any section that lists problems uses this
//     prop — never its own red.

import Chevron from './Chevron.jsx';
import { WARN } from '../icons.js';

export default function SectionTitle({ children, count, right, collapsed, onToggle, na = false, naReason = null, locked = false, icon = null, collapsedLabel = null, problem = false }) {
  const isProblem = problem && !na;
  const titleStyle = {
    fontSize: 'var(--fs-small)', fontWeight: 700,
    color: na ? 'var(--th-text-disabled)' : isProblem ? 'var(--th-status-deprecated)' : 'var(--th-text-body)',
    textTransform: 'uppercase', letterSpacing: 0.5,
  };
  // The title text, with the ⚠ glyph in front when this section holds problems.
  const label = isProblem ? <><span aria-hidden="true">{WARN}</span> {children}</> : children;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
      {/* The item's own picture, BEFORE the title (Alex, 2026-08-27: "can the
          icon be added to the Catalog, Product & 3D model title"). It sits
          outside the chevron/button so it is never part of the collapse target:
          a picture that swallows clicks meant for the disclosure control would
          make the one thing on this row that IS interactive harder to hit.
          Renders nothing when there is no picture — ImageCell already returns
          null — so no call site needs to branch and no title gains a gap. */}
      {icon}
      {na ? (
        <>
          {/* Chevron shown greyed rather than omitted (Alex, 2026-08-21: "I would
              like still to see the close chevron grey out as well") — closed,
              non-interactive, colour-inherited from this span so it mutes the
              same way the title does. The #NA word still carries the state on
              its own (colour-blind-safe); the chevron here is purely visual
              consistency with every collapsible title having one. */}
          <span style={{ ...titleStyle, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Chevron open={false} />
            {children}
          </span>
          <span
            title={naReason || undefined}
            style={{ fontSize: 'var(--fs-small)', fontWeight: 400, textTransform: 'none', color: 'var(--th-text-disabled)' }}
          >#NA</span>
        </>
      ) : onToggle ? (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={!collapsed}
          title={collapsed ? 'Show this section' : 'Hide this section'}
          style={{
            ...titleStyle, display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          <Chevron open={!collapsed} />
          {label}
          {collapsed && collapsedLabel && (
            <span style={{ fontSize: 'var(--fs-small)', fontWeight: 400, textTransform: 'none', color: 'var(--th-accent)' }}>
              — {collapsedLabel}
            </span>
          )}
        </button>
      ) : locked ? (
        <span style={{ ...titleStyle, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Chevron open />
          {label}
        </span>
      ) : (
        <span style={titleStyle}>{label}</span>
      )}
      {/* Count rides OUTSIDE the uppercase/bold span — "3 items" reads as a
          plain number+word, not shouted like the title (textTransform
          inherits from the parent otherwise: "(3 ITEMS)"). */}
      {count != null && (
        <span style={{
          fontSize: 'var(--fs-small)', textTransform: 'none',
          fontWeight: isProblem ? 700 : 400,
          color: isProblem ? 'var(--th-status-deprecated)' : 'var(--th-text-disabled)',
        }}>
          ({count})
        </span>
      )}
      {right}
    </div>
  );
}
